import React from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';

export type LazyableImageProps = {
    src: string;
    width: number;
    height: number;
    alt: string;
    placeholderSrc?: string;
    notLazy?: boolean;
    className?: string;
    // LCP候補画像（notLazy指定時のみ有効）にfetchpriorityを付与する。
    // @types/react 17.0.90はfetchPriorityをLinkHTMLAttributesにのみ定義しており
    // ImgHTMLAttributesには無い（index.d.ts:2327、img向け追加は将来バージョン）ため、
    // <img>への直接指定は型エラーになる。react-dom 17.0.2側もfetchPriorityを未知の
    // プロパティとして扱い開発時に警告を出す（react-dom.development.js:2855の
    // setValueForProperty検証）。SSR文字列化自体はcamelCaseのまま出力する
    // （実測: react-dom-server.node.development.js:1261の
    // createMarkupForProperty、renderToStaticMarkupで<img fetchPriority="high">
    // を確認）が、HTML仕様上属性名は大文字小文字を区別しないため、ブラウザが
    // このSSR HTMLをパースしてDOMを構築する時点でfetchpriorityに正規化される。
    // 結果として最終的なDOM上の属性名はfetchpriorityで一致する
    // （動作は正しい、開発時コンソール警告のみ）。
    // ref: https://developer.mozilla.org/docs/Web/HTML/Reference/Attributes/fetchpriority
    fetchPriority?: 'high';
};

// ImgHTMLAttributesが未対応のfetchPriorityを型エラーなく渡すための拡張型
// （中間変数を介したspreadにすることで、オブジェクトリテラル直書き時の
// 過剰プロパティチェックを回避する）
type ImgPropsWithFetchPriority = React.ImgHTMLAttributes<HTMLImageElement> & {
    fetchPriority?: 'high';
};

const LazyableImage = (props: LazyableImageProps) => {
    if (props.notLazy) {
        const imgProps: ImgPropsWithFetchPriority = {
            className: props.className,
            src: props.src,
            width: props.width,
            height: props.height,
            alt: props.alt,
            fetchPriority: props.fetchPriority,
        };
        return <img {...imgProps} />;
    }

    return (
        // ref: https://github.com/Aljullu/react-lazy-load-image-component
        <LazyLoadImage
            className={props.className}
            src={props.src}
            width={props.width}
            height={props.height}
            alt={props.alt}
            placeholderSrc={props.placeholderSrc}
        />
    );
};

export default LazyableImage;
